/**
 * Shared Pagination component with smart truncation.
 * Shows: 1 2 3 ... 14 15 16 ... 31 32  (window around current page + first/last)
 */
export default function Pagination({ currentPage, totalPages, onPageChange, itemsPerPage, onItemsPerPageChange, totalItems }) {

  // Build the page number array with ellipsis markers
  function getPageNumbers() {
    if (totalPages <= 7) {
      // Not enough pages to need truncation
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [];
    const delta = 2; // pages on each side of current

    const left = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    // Always show first page
    pages.push(1);

    // Left ellipsis
    if (left > 2) pages.push('...');

    // Window around current page
    for (let i = left; i <= right; i++) {
      pages.push(i);
    }

    // Right ellipsis
    if (right < totalPages - 1) pages.push('...');

    // Always show last page
    pages.push(totalPages);

    return pages;
  }

  const pageNumbers = getPageNumbers();

  const containerStyle = {
    display: 'inline-flex',
    alignItems: 'stretch',
    borderRadius: '4px',
    border: '1px solid var(--border)',
    background: 'var(--panel)',
    overflow: 'hidden',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
  };

  const btnSegment = {
    background: 'var(--panel)',
    color: 'var(--text)',
    border: 'none',
    borderRight: '1px solid var(--border)',
    padding: '6px 14px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.15s ease, filter 0.15s ease',
    minWidth: '36px'
  };

  const btnActive = {
    ...btnSegment,
    background: '#8c211e',
    color: '#ffffff',
    fontWeight: 600,
    cursor: 'default'
  };

  const btnDisabled = {
    color: 'var(--muted)',
    background: 'transparent',
    opacity: 0.5,
    cursor: 'not-allowed'
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mt-4 px-1">
      {/* Left: Show entries selector */}
      <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--muted)' }}>
        {onItemsPerPageChange && (
          <>
            <span>Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className="border rounded px-2 py-1 text-sm"
              style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
            >
              {[5, 10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span>entries</span>
          </>
        )}
      </div>

      {/* Center: Segmented Page buttons */}
      <div style={containerStyle}>
        {/* Previous */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            ...btnSegment,
            ...(currentPage === 1 ? btnDisabled : {})
          }}
          onMouseEnter={(e) => {
            if (currentPage !== 1) e.currentTarget.style.opacity = '0.8';
          }}
          onMouseLeave={(e) => {
            if (currentPage !== 1) e.currentTarget.style.opacity = '1';
          }}
        >
          Previous
        </button>

        {pageNumbers.map((num, idx) =>
          num === '...' ? (
            <span
              key={`ellipsis-${idx}`}
              style={{
                ...btnSegment,
                color: 'var(--muted)',
                cursor: 'default',
                userSelect: 'none'
              }}
            >
              …
            </span>
          ) : (
            <button
              key={num}
              onClick={() => onPageChange(num)}
              style={currentPage === num ? btnActive : btnSegment}
              onMouseEnter={(e) => {
                if (currentPage !== num) e.currentTarget.style.opacity = '0.8';
              }}
              onMouseLeave={(e) => {
                if (currentPage !== num) e.currentTarget.style.opacity = '1';
              }}
            >
              {num}
            </button>
          )
        )}

        {/* Next (last item has no right border) */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
          style={{
            ...btnSegment,
            borderRight: 'none',
            ...(currentPage === totalPages || totalPages === 0 ? btnDisabled : {})
          }}
          onMouseEnter={(e) => {
            if (currentPage !== totalPages && totalPages > 0) e.currentTarget.style.opacity = '0.8';
          }}
          onMouseLeave={(e) => {
            if (currentPage !== totalPages && totalPages > 0) e.currentTarget.style.opacity = '1';
          }}
        >
          Next
        </button>
      </div>

      {/* Right: Showing X to Y of Z */}
      {totalItems != null && itemsPerPage != null && (
        <div className="text-sm" style={{ color: 'var(--muted)' }}>
          Showing{' '}
          <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}</span>
          {' '}to{' '}
          <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span>
          {' '}of{' '}
          <span className="font-medium">{totalItems}</span> entries
        </div>
      )}
    </div>
  );
}
